import {
  ExchangeClient,
  HttpTransport,
  InfoClient,
  type ISubscription,
  SubscriptionClient,
  WebSocketTransport,
} from '@nktkas/hyperliquid';
import type { SubscriptionOptions } from '@nktkas/hyperliquid/api/subscription';
import type { AbstractWallet } from '@nktkas/hyperliquid/signing';

import { logger } from '@/lib/logger';

import { JWT_PROTECTED_HYPERCORE_API_URL } from '@/constants/rpc';

import { getNodeProviderJwt } from '@/hooks/useNodeProviderToken';

import { HYPERLIQUID_API_URL, IS_HYPERLIQUID_TESTNET } from './constants';

/**
 * Routes `/info` reads through QuickNode with the rotating node-provider JWT. The JWT
 * lives in localStorage and refreshes every few minutes, so it is attached per request
 * rather than at construction. Mirrors wagmi's transport: before the first JWT lands
 * (or if it lapses) the request goes to Hyperliquid directly instead of failing.
 */
class QuickNodeInfoTransport extends HttpTransport {
  request<T>(endpoint: 'info' | 'exchange' | 'explorer', payload: unknown, signal?: AbortSignal) {
    try {
      this.fetchOptions = { headers: { Authorization: `Bearer ${getNodeProviderJwt()}` } };
      this.apiUrl = JWT_PROTECTED_HYPERCORE_API_URL;
    } catch {
      this.fetchOptions = {};
      this.apiUrl = HYPERLIQUID_API_URL;
    }
    return super.request<T>(endpoint, payload, signal);
  }
}

// QuickNode serves HyperCore mainnet only on our endpoint; testnet stays on Hyperliquid.
const infoTransport = IS_HYPERLIQUID_TESTNET
  ? new HttpTransport({ isTestnet: true })
  : new QuickNodeInfoTransport();
// Signed actions go to Hyperliquid's own `/exchange` (see JWT_PROTECTED_HYPERCORE_API_URL).
const exchangeTransport = new HttpTransport({ isTestnet: IS_HYPERLIQUID_TESTNET });

export const hyperliquidInfo = new InfoClient({ transport: infoTransport });

let subscriptionClient: SubscriptionClient | undefined;

/**
 * Lazily opens the shared WebSocket. The transport reconnects on its own and
 * refcounts listeners per subscription payload, so callers can subscribe and
 * unsubscribe freely without coordinating with each other.
 */
export function getHyperliquidSubscriptions(): SubscriptionClient {
  subscriptionClient ??= new SubscriptionClient({
    transport: new WebSocketTransport({ isTestnet: IS_HYPERLIQUID_TESTNET }),
  });
  return subscriptionClient;
}

/**
 * Opens a subscription and returns the cleanup for a `useEffect`. Retries failed
 * subscriptions with backoff while mounted; the SDK handles ordinary socket reconnects.
 * Callers must forward the options to the SDK subscription method.
 */
export function subscribeWhileMounted(
  subscribe: (client: SubscriptionClient, options: SubscriptionOptions) => Promise<ISubscription>,
): () => void {
  let subscription: ISubscription | undefined;
  let controller: AbortController | undefined;
  let retryTimeout: ReturnType<typeof setTimeout> | undefined;
  let retryDelay = 1_000;

  const unsubscribe = (sub: ISubscription) => {
    void sub
      .unsubscribe()
      .catch((error: unknown) => logger.warn('Hyperliquid subscription cleanup failed', { error }));
  };

  async function connect() {
    const attempt = new AbortController();
    controller = attempt;
    // Per the SDK, an attempt fails at most once: the subscribe promise rejects before
    // confirmation, or `onError` fires after it. Aborting marks the attempt as settled.
    const retry = (error: unknown) => {
      if (attempt.signal.aborted) return;
      attempt.abort();
      subscription = undefined;
      logger.warn('Hyperliquid subscription failed', { error });
      retryTimeout = setTimeout(() => void connect(), retryDelay);
      retryDelay = Math.min(retryDelay * 2, 30_000);
    };

    try {
      const sub = await subscribe(getHyperliquidSubscriptions(), {
        signal: attempt.signal,
        onError: retry,
      });
      if (attempt.signal.aborted) return unsubscribe(sub);
      subscription = sub;
      retryDelay = 1_000;
    } catch (error) {
      retry(error);
    }
  }

  void connect();

  return () => {
    clearTimeout(retryTimeout);
    controller?.abort();
    if (subscription) unsubscribe(subscription);
    subscription = undefined;
  };
}

/**
 * Exchange client bound to a signer. Pass the user's wallet client for
 * user-signed actions (approveAgent, approveBuilderFee, withdraw) and the
 * local agent account for L1 actions (order, cancel, updateLeverage).
 */
export function createHyperliquidExchange(wallet: AbstractWallet): ExchangeClient {
  return new ExchangeClient({ transport: exchangeTransport, wallet });
}
